import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from './AuthContext'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/components/controls/LanguageSwitcher'
import { Sparkles, ArrowRight, AlertCircle } from 'lucide-react'
import { isProd } from '@/lib/env'

const DEMO_HINTS = [
  { email: 'admin@objedname.eu', password: 'ordermage', tenant: 'Objednáme · OrderMage' },
  { email: 'demo@demo.cz', password: 'demo', tenant: 'Demo Bistro Group' },
]

export function LoginView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login, loading, error } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    try {
      await login(email, password)
      navigate('/', { replace: true })
    } catch {
      
    }
  }

  function fill(d: (typeof DEMO_HINTS)[number]) {
    setEmail(d.email)
    setPassword(d.password)
  }

  return (
    <div
      className="relative grid size-full place-items-center overflow-hidden bg-surface-0 px-6"
      data-testid="login-view"
    >
      {}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 size-[40rem] rounded-full bg-[radial-gradient(closest-side,var(--brand-primary)_0%,transparent_70%)] opacity-20" />
        <div className="absolute -bottom-40 -right-32 size-[36rem] rounded-full bg-[radial-gradient(closest-side,var(--brand-accent)_0%,transparent_70%)] opacity-15" />
      </div>

      <div className="absolute right-6 top-6 z-10">
        <LanguageSwitcher />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative z-10 grid w-full max-w-5xl items-center gap-12 lg:grid-cols-[1.1fr_1fr]"
      >
        {}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-border-strong/40 bg-surface-1/60 px-4 py-1.5 text-sm text-ink-muted backdrop-blur">
            <Sparkles className="size-3.5 text-brand-accent" />
            <span>{t('login.badge')}</span>
          </div>
          <h1 className="font-display text-5xl font-bold tracking-tight text-ink lg:text-6xl">
            {t('login.title.line1')}
            <br />
            <span className="bg-gradient-to-r from-[var(--brand-primary)] via-[var(--brand-primary)] to-[var(--brand-accent)] bg-clip-text text-transparent">
              {t('login.title.line2')}
            </span>
          </h1>
          <p className="max-w-xl text-lg leading-relaxed text-ink-muted">{t('login.subtitle')}</p>
          <ul className="grid gap-3 text-sm text-ink-muted">
            {(
              [
                'feature.realtime',
                'feature.milestones',
                'feature.celebrations',
                'feature.multitenant',
              ] as const
            ).map((k) => (
              <li key={k} className="flex items-start gap-3">
                <span className="mt-1 size-1.5 rounded-full bg-brand-primary" />
                <span>{t(`login.${k}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        {}
        <form
          onSubmit={onSubmit}
          className="rounded-3xl border border-border bg-surface-1/80 p-8 shadow-2xl shadow-black/20"
        >
          <h2 className="mb-6 font-display text-2xl font-semibold text-ink">
            {t('login.form.title')}
          </h2>
          <div className="space-y-4">
            <Field
              label={t('login.form.email')}
              type="email"
              autoComplete="username"
              value={email}
              onChange={setEmail}
              placeholder="email@example.com"
              required
            />
            <Field
              label={t('login.form.password')}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
              required
            />

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-xl border border-signal-bad/40 bg-signal-bad/10 px-3 py-2 text-sm text-[color:var(--signal-bad)]"
              >
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{t('login.form.invalid')}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-3 font-semibold text-ink-inverse transition hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
            >
              {loading ? t('login.form.loading') : t('login.form.submit')}
              <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
            </button>
          </div>

          {!isProd && (
          <div className="mt-6 space-y-2 border-t border-border pt-5">
            <p className="text-xs uppercase tracking-widest text-ink-subtle">
              {t('login.form.demoTitle')}
            </p>
            <div className="grid gap-2">
              {DEMO_HINTS.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fill(d)}
                  className="rounded-lg border border-border bg-surface-2/60 px-3 py-2 text-left text-sm text-ink-muted transition hover:border-brand-primary/50 hover:text-ink"
                >
                  <div className="font-medium text-ink">{d.tenant}</div>
                  <div className="font-mono text-xs text-ink-subtle">
                    {d.email} · {d.password}
                  </div>
                </button>
              ))}
            </div>
          </div>
          )}
        </form>
      </motion.div>
    </div>
  )
}

interface FieldProps {
  label: string
  type: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
  autoComplete?: string
}

function Field({ label, type, value, onChange, placeholder, required, autoComplete }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-widest text-ink-subtle">
        {label}
      </span>
      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-base text-ink outline-none transition placeholder:text-ink-subtle focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30"
      />
    </label>
  )
}
